/* KCI Event Tagging — shared presentational kit.
   Aligns every KCI screen to the current Admin-panel look used across Forward
   Shipment / Courier Master / Order Details: soft-shadow card shells, tinted
   DataGrid headers with roomy rows, uppercase stat tiles, and the same
   navy/muted type ramp. Keep visual tokens here so the four KCI pages stay in
   sync with each other and with the rest of the panel. */
import React from 'react'
import { Box, Typography } from '@mui/material'

// The cached courier-partner list can contain duplicate cpAccountCodes (junk/test
// rows). Duplicate option values → duplicate React keys in the dropdown, which
// corrupts list reconciliation while filtering (stale, non-matching rows linger).
// De-dupe by cpAccountCode before building options.
export const dedupeCouriers = (couriers) => {
  const seen = new Set()
  const out = []
  for (const c of couriers || []) {
    if (!c?.cpAccountCode || seen.has(c.cpAccountCode)) continue
    seen.add(c.cpAccountCode)
    out.push(c)
  }
  return out
}

// Shared filter for the panel's cached courier-partner list (courierPartner/
// searchCached via useGetAllCourierPartnersCachedQuery) — the source every admin
// screen uses for courier dropdowns. Returns cpAccountCode / name / parent, which
// is what all KCI courier pickers read.
export const COURIER_LIST_FILTER = { offset: 0, limit: 1000, skipCount: true, includes: 'id,name,cpAccountCode,prozoId,parent,serviceTypePrimary,serviceTypeSecondary' }

// ── Courier chain (EXACT copy of the D2C Forward Shipment filter cascade) ───────
// parent → serviceType[] → accountCode[]. Resolution is client-side (same as the
// Forward Shipment page): pick a parent + service type(s), then optionally narrow
// to specific account codes; the value ultimately used is cpAccountCode(s).
export const ALL_SERVICE_TYPES = '__ALL_SERVICE_TYPES__'

export const kciCourierParents = (couriers) =>
  Array.from(new Set(dedupeCouriers(couriers).map((c) => c.parent).filter(Boolean)))
    .sort()
    .map((p) => ({ value: p, label: p }))

// Distinct service types under a parent, with a leading "All Service Types" option.
export const kciServiceTypes = (couriers, parent) => {
  if (!parent) return []
  const set = new Set()
  dedupeCouriers(couriers).filter((c) => c.parent === parent).forEach((c) => {
    if (c.serviceTypePrimary) set.add(c.serviceTypePrimary)
    if (c.serviceTypeSecondary) set.add(c.serviceTypeSecondary)
  })
  return [{ value: ALL_SERVICE_TYPES, label: 'All Service Types' },
    ...Array.from(set).sort().map((s) => ({ value: s, label: s }))]
}

// Courier accounts matching parent + service type(s). Empty/ALL service type = every
// account under the parent (mirrors AllOrders' resolution).
export const kciMatchingCouriers = (couriers, parent, serviceTypes) => {
  if (!parent) return []
  const sts = Array.isArray(serviceTypes) ? serviceTypes : (serviceTypes ? [serviceTypes] : [])
  const all = sts.length === 0 || sts.includes(ALL_SERVICE_TYPES)
  return dedupeCouriers(couriers).filter((c) => {
    if (c.parent !== parent) return false
    if (all) return true
    return (c.serviceTypePrimary && sts.includes(c.serviceTypePrimary)) ||
      (c.serviceTypeSecondary && sts.includes(c.serviceTypeSecondary))
  })
}
export const kciAccountOptions = (couriers, parent, serviceTypes) =>
  kciMatchingCouriers(couriers, parent, serviceTypes)
    .map((c) => ({ value: c.cpAccountCode, label: c.name ? `${c.name} (${c.cpAccountCode})` : c.cpAccountCode }))

// Final cpAccountCode list: the chosen account codes, or (if none chosen) every
// account under parent + service type — exactly like the Forward Shipment resolve.
export const resolveKciCourierCodes = (couriers, { parent, serviceTypes, accountCodes }) => {
  if (!parent) return []
  const matching = kciMatchingCouriers(couriers, parent, serviceTypes).map((c) => c.cpAccountCode)
  const picked = Array.isArray(accountCodes) ? accountCodes.filter(Boolean) : (accountCodes ? [accountCodes] : [])
  return picked.length ? picked.filter((x) => matching.includes(x)) : matching
}

// yyyy-MM-dd ⇄ Date (local, to avoid the UTC off-by-one when feeding the range picker)
export const toKey = (d) => { const x = new Date(d); return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}` }
export const parseKey = (s) => { const [y, m, d] = String(s).split('-').map(Number); return new Date(y, m - 1, d) }

// ── Design tokens ─────────────────────────────────────────────────────────────
export const NAVY = '#212B36'
export const MUTED = '#637381'
export const BORDER = '#E7EAF0'

// ── Dropdown option separators (mirrors the D2C Forward-Shipment courier dropdown:
//    RHFSearchableSelectField / RHFMultipleSelectField — a 1px #e8e8e8 line under
//    every option). Use these on ALL KCI dropdowns so they read identically.
// Autocomplete: renderOption={sepOption((o) => labelFor(o), (o) => keyFor(o))}
export const kciOptionStyle = { color: NAVY, fontFamily: 'Poppins, sans-serif', fontSize: '13px', borderBottom: '1px solid #e8e8e8', padding: '8px 12px' }
export const sepOption = (getLabel, getKey) => (props, option) => (
  <li {...props} key={getKey ? getKey(option) : String(getLabel(option))} style={kciOptionStyle}>{getLabel(option)}</li>
)
// TextField select: <TextField select SelectProps={{ MenuProps: sepMenuProps }}> with each <MenuItem sx={sepMenuItemSx}>
export const sepMenuItemSx = { fontSize: 13, borderBottom: '1px solid #e8e8e8', '&:last-of-type': { borderBottom: 'none' } }
export const sepMenuProps = { PaperProps: { sx: { '& .MuiList-root': { py: 0 } } } }

// Soft-shadow card shell — the panel-wide surface used for every filter bar,
// chart card and grid wrapper.
export const cardSx = {
  bgcolor: '#fff',
  border: `1px solid ${BORDER}`,
  borderRadius: '10px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
}

// Tinted header + roomy cells — mirrors the Courier Master / Forward Shipment grids.
export const gridSx = {
  '& .MuiDataGrid-columnHeaders': { backgroundColor: '#F9FAFB', borderBottom: `1px solid #E2E8F0` },
  '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 700, color: '#475569' },
  '& .MuiDataGrid-cell': { paddingTop: '10px', paddingBottom: '10px' },
}

// White rounded box the DataGrid sits inside (grid rendered `flat`).
export const gridWrapSx = { ...cardSx, overflow: 'hidden' }

// ── Coloured grid pills (the colour vocabulary the reverse-orders grid uses) ──
// direction tone: Forward=blue, Reverse=amber, Both=purple.
export const dirTone = (d) => ({ FORWARD: { bg: '#e8f1fc', fg: '#1565c0' }, REVERSE: { bg: '#fdf2e5', fg: '#b45309' }, BOTH: { bg: '#f3edfb', fg: '#6d28d9' } }[d] || { bg: '#eef1f5', fg: '#475569' })
// tinted rounded badge with an optional leading dot
export const Pill = ({ tone, dot, dotColor, children }) => (
  <Box component='span' sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.6, px: 0.9, py: 0.25, borderRadius: '6px', fontFamily: 'Poppins, sans-serif', fontSize: 11, fontWeight: 700, lineHeight: 1.5, bgcolor: tone.bg, color: tone.fg }}>
    {dot && <Box component='span' sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: dotColor || tone.fg }} />}
    {children}
  </Box>
)

// ── Event geography coverage text ────────────────────────────────────────────
// Listing rows (GET /event/search) now return `pincodeCount` and OMIT the
// `pincodes` array (a leg can hold 25k entries); the detail endpoint still
// returns the full array — support both. Either leg may be absent = "no filter
// on that leg", so render only the present leg(s).
export const legCoverage = (g) => {
  if (!g) return null
  if (g.geographyType === 'PAN_INDIA' || g.panIndia) return 'Pan-India'
  const count = g.pincodeCount ?? g.pincodes?.length ?? 0
  if (g.selectedStates?.length) return `${g.selectedStates[0]}${g.selectedStates.length > 1 ? ` +${g.selectedStates.length - 1}` : ''} (${count} pin)`
  if (g.selectedCities?.length) return `${g.selectedCities[0]}${g.selectedCities.length > 1 ? ` +${g.selectedCities.length - 1}` : ''} (${count} pin)`
  return `${count} pin`
}
export const coverage = (e) => {
  const parts = []
  if (e.pickupGeography) parts.push(`Pickup: ${legCoverage(e.pickupGeography)}`)
  if (e.dropGeography) parts.push(`Drop: ${legCoverage(e.dropGeography)}`)
  if (parts.length) return parts.join(' · ')
  // legacy (pre-per-leg) flat shape
  if (e.geographyType === 'PAN_INDIA' || e.panIndia) return 'Pan-India'
  const count = e.pincodeCount ?? e.pincodes?.length ?? 0
  return `${count} pin`
}
// just the region name (no pin count) for one leg — null for a plain pincode list
// (the header already says "Pincodes", so we only show a name for state/city/pan-india)
const legRegionName = (g) => {
  if (!g) return null
  if (g.geographyType === 'PAN_INDIA' || g.panIndia) return 'Pan-India'
  if (g.selectedStates?.length) return `${g.selectedStates[0]}${g.selectedStates.length > 1 ? ` +${g.selectedStates.length - 1}` : ''}`
  if (g.selectedCities?.length) return `${g.selectedCities[0]}${g.selectedCities.length > 1 ? ` +${g.selectedCities.length - 1}` : ''}`
  return null
}
const pinBadgeSx = { display: 'inline-flex', alignItems: 'center', px: 0.7, py: '1px', borderRadius: '5px', fontSize: 10, fontWeight: 700, bgcolor: '#F1F3F7', color: '#637381', fontFamily: 'Poppins, sans-serif', lineHeight: 1.5, whiteSpace: 'nowrap' }
const legLabelSx = (tone) => ({ display: 'inline-flex', alignItems: 'center', px: 0.7, py: '1px', borderRadius: '5px', fontSize: 9.5, fontWeight: 700, letterSpacing: '.03em', textTransform: 'uppercase', bgcolor: tone.bg, color: tone.fg, fontFamily: 'Poppins, sans-serif', lineHeight: 1.5 })
// Modern coverage cell: one row per leg — a coloured Pickup/Drop tag, the region
// name, and a subtle pin-count badge. Falls back to the legacy flat shape.
export const CoverageCell = ({ event: e }) => {
  const legs = []
  if (e.pickupGeography) legs.push({ key: 'pickup', label: 'Pickup', tone: { bg: '#e8f1fc', fg: '#1565c0' }, g: e.pickupGeography })
  if (e.dropGeography) legs.push({ key: 'drop', label: 'Drop', tone: { bg: '#e4f3f1', fg: '#0f766e' }, g: e.dropGeography })
  const legRow = (label, tone, g) => {
    const isPan = g.geographyType === 'PAN_INDIA' || g.panIndia
    const count = g.pincodeCount ?? g.pincodes?.length
    const region = legRegionName(g)
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
        {label && <Box component='span' sx={legLabelSx(tone)}>{label}</Box>}
        {region && <Typography component='span' sx={{ fontSize: 12, color: NAVY, fontWeight: 500 }}>{region}</Typography>}
        {!isPan && count != null && <Box component='span' sx={{ ...pinBadgeSx, fontSize: 12, color: NAVY, bgcolor: 'transparent', px: 0, fontWeight: 600 }}>{count.toLocaleString()}</Box>}
      </Box>
    )
  }
  if (legs.length === 0) return legRow(null, {}, e) // legacy flat shape
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, py: 0.5 }}>
      {legs.map((l) => <React.Fragment key={l.key}>{legRow(l.label, l.tone, l.g)}</React.Fragment>)}
    </Box>
  )
}

// ── Rich table cells (Forward Shipment pattern) ───────────────────────────────
export const CellLine = ({ children, muted, bold, ellipsis }) => (
  <Box sx={{
    fontSize: 12.5, lineHeight: 1.4,
    color: muted ? MUTED : NAVY,
    fontWeight: bold ? 600 : 400,
    ...(ellipsis
      ? { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }
      : { whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere' }),
  }}>{children}</Box>
)
export const CellStack = ({ children }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.35, width: '100%', py: 0.5 }}>{children}</Box>
)

// ── Page heading — bold navy title + muted subtitle (matches merchant panel) ──
export const PageHeading = ({ title, subtitle, action }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 2 }}>
    <Box>
      <Typography sx={{ fontSize: 20, fontWeight: 700, color: NAVY, fontFamily: 'Poppins, sans-serif', lineHeight: 1.2 }}>{title}</Typography>
      {subtitle && <Typography sx={{ fontSize: 13, color: MUTED, mt: 0.25 }}>{subtitle}</Typography>}
    </Box>
    {action}
  </Box>
)

// ── Card shell with optional title / action row ───────────────────────────────
export const SectionCard = ({ title, action, children, sx, pad = 2 }) => (
  <Box sx={{ ...cardSx, p: pad, height: '100%', ...sx }}>
    {(title || action) && (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        {title && <Typography sx={{ fontSize: 13, fontWeight: 700, color: NAVY }}>{title}</Typography>}
        {action}
      </Box>
    )}
    {children}
  </Box>
)

// ── Stat tile — uppercase label, big value, colored left accent ───────────────
export const StatTile = ({ label, value, sub, accent = '#1565c0', highlight }) => (
  <Box sx={{
    p: 2,
    borderRadius: '10px',
    bgcolor: '#fff',
    height: '100%',
    borderLeft: `4px solid ${accent}`,
    border: highlight ? `1.5px solid ${accent}` : `1px solid ${BORDER}`,
    borderLeftWidth: 4,
    borderLeftColor: accent,
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  }}>
    <Typography sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', color: '#8A94A6' }}>{label}</Typography>
    <Typography sx={{ fontSize: 25, fontWeight: 700, color: highlight ? accent : NAVY, lineHeight: 1.2, mt: 0.5 }}>{value}</Typography>
    {sub && <Typography sx={{ fontSize: 11.5, color: '#8A94A6', mt: 0.25 }}>{sub}</Typography>}
  </Box>
)

// ── Error message extractor ───────────────────────────────────────────────────
// kciApiSlice's `run` puts the backend message on error.message and the full
// body on error.data. Surface the server's own message (e.g. "KCI_MANAGEMENT
// access required") verbatim, falling back only when there isn't one.
export const errMsg = (e, fallback = 'Something went wrong.') =>
  (typeof e?.data?.message === 'string' && e.data.message) ||
  (typeof e?.message === 'string' && e.message) ||
  fallback

// ── Button styling shared with the panel (blue primary, red reset) ────────────
export const primaryBtnSx = {
  textTransform: 'none', fontWeight: 600, bgcolor: '#1565c0', color: '#fff',
  boxShadow: 'none', '&:hover': { bgcolor: '#0d47a1', boxShadow: 'none' },
}
export const softBtnSx = {
  textTransform: 'none', fontWeight: 600, bgcolor: '#e3f2fd', color: '#1565c0',
  boxShadow: 'none', '&:hover': { bgcolor: '#1565c0', color: '#fff', boxShadow: 'none' },
}
